import { CDataTable } from "@coreui/react";
import React, { useEffect } from "react";
import {
  DisplayRichText,
  InfoTooltip,
  SquareButtonOutline,
} from "src/components";
import ReactSpeedometer from "react-d3-speedometer";
import { calculateFinalTotals } from "src/features/esg-survey/utils/assessment-utils";
import { NextQuestionModal } from "..";
import styled from "styled-components";

const COLUMNS = [
  { key: "idx", label: "" },
  "question",
  { key: "self-score", label: "Your answer" },
  // { key: "admin-score", label: "Admin score", _style: { width: "12%" } },
  // { key: "weight", label: "Weight", _style: { width: "12%" } },

  // {
  //   key: "external-average",
  //   label: "External Score",
  //   _style: { width: "15%" },
  // },
  {
    key: "final-score",
    label: "Score",
    _style: { width: "15%" },
  },
  {
    key: "next",
    label: "What's next?",
  },
];

const PRINT_COLUMNS = [
  "idx",
  "question",
  { key: "self-score", label: "Your answer" },
  {
    key: "final-score",
    label: "Score",
    _style: { width: "30%" },
  },
];

const StyledTd = styled.td`
  p {
    font-size: 17px;
    font-weight: 500;
  }
`;

function clearAllTagsFromText(text) {
  return text?.replace(/<[^>]*>?/gm, "");
}

function addLineBreak(text) {
  return text?.replace(/\)/gm, "): ");
}

const SurveyTable = ({ rows, isPrint = false, addTableClasses }) => {
  useEffect(() => {
    const table = document.querySelector(".esg-table");
    table?.setAttribute("id", "esg-table");
  }, []);

  const [modalQuestion, setModalQuestion] = React.useState(null);

  const handleClose = () => {
    setModalQuestion(null);
  };

  const { totalScore, totalPercentage } = calculateFinalTotals(rows);

  const totalRow = {
    question: "Total",
    idx: "",
    "self-score": totalScore + " points",
    "final-score": totalPercentage + " %",
    _classes: "table-sector",
  };

  const rowsToUse = isPrint ? rows : [...rows, totalRow];

  return (
    <>
      <CDataTable
        addTableClasses={
          (isPrint ? "invisible position-absolute esg-table " : "") +
          addTableClasses
        }
        fields={isPrint ? PRINT_COLUMNS : COLUMNS}
        items={rowsToUse}
        columnHeaderSlot={{
          idx: (item, index) => {
            return (
              <StyledTd>
                <span className="font-bold">{item.idx}</span>
              </StyledTd>
            );
          },
          question: (item, index) => {
            return (
              <StyledTd>
                <DisplayRichText value={item.question} />
              </StyledTd>
            );
          },
          "external-average": (
            <div className="d-flex">
              <span>External score</span>
              <InfoTooltip
                content={
                  "If more than 3 responses - average of central 3, otherwise average of all responses"
                }
              />
            </div>
          ),
          weight: (
            <div className="d-flex">
              <span>Weight</span>
              <InfoTooltip
                content={"Question weight between 1-5, assigned by Admin"}
              />
            </div>
          ),
        }}
        scopedSlots={{
          question: (item, index) => {
            return (
              <td>
                <DisplayRichText value={item.question} />
              </td>
            );
          },
          details: !isPrint
            ? (item) => {
                if (!item.adminComment) return null;
                return (
                  <td
                    style={{
                      border: "none",
                    }}
                  >
                    <span className="font-semibold text-muted">
                      Admin comment:
                    </span>{" "}
                    {item.adminComment}
                  </td>
                );
              }
            : null,
          "self-score": (q, idx) => {
            return (
              <td>
                {isPrint ? (
                  <div>
                    {addLineBreak(
                      clearAllTagsFromText(q["self-score"] || q.answer)
                    )}
                  </div>
                ) : q.type === "select" ? (
                  <>
                    {typeof q["self-score"] === "string" ? (
                      <DisplayRichText value={q["self-score"]} />
                    ) : (
                      q["self-score"]
                    )}
                  </>
                ) : q.type === "text" ? (
                  <DisplayRichText value={q.answer} />
                ) : q.type === "number" ||
                  q.type === "date" ||
                  q.type === "date-range" ? (
                  <span>{q.answer}</span>
                ) : null}
              </td>
            );
          },

          "admin-score": (item) => {
            const hasAdminScore = item["admin-score"] !== "n/a";

            const adminScoreIsLower = item["admin-score"] < item["self-value"];
            const adminScoreisEqual =
              item["admin-score"] === item["self-value"];

            let color = adminScoreIsLower
              ? "text-danger"
              : adminScoreisEqual
              ? ""
              : "text-success";

            item.question === "Total" && (color = "");

            return (
              <td>
                {hasAdminScore && (
                  <span className={`${color} font-bold`}>
                    {item["admin-score"]}
                  </span>
                )}
              </td>
            );
          },
          weight: (item, idx) => {
            return (
              <td>
                {!!item.weight &&
                  Array(+item.weight)
                    .fill(0)
                    .map((i) => (
                      <span key={Math.random()} className="dot ml-1"></span>
                    ))}
              </td>
            );
          },
          next: (item, idx) => {
            const isQuestion = item._classes.includes("table-question");
            return (
              <td>
                {isQuestion ? (
                  <SquareButtonOutline
                    disabled={!item.nextAnswerLabel}
                    style={{ width: "65px" }}
                    onClick={() => setModalQuestion(item)}
                  >
                    Next
                  </SquareButtonOutline>
                ) : (
                  <></>
                )}
              </td>
            );
          },
          "final-score": (item, idx) => {
            const isQuestion = item._classes.includes("table-question");

            const value = isQuestion
              ? isNaN(+item["final-score"])
                ? 0
                : +item["final-score"]
              : item["final-score"];

            return (
              <td>
                {isQuestion ? (
                  <ReactSpeedometer
                    width={150}
                    height={150}
                    value={value}
                    maxValue={item.maxScore}
                    needleColor="darkred"
                    startColor="orange"
                    endColor="darkgreen"
                    maxSegmentLabels={0}
                    paddingHorizontal={2}
                    valueTextFontSize="18px"
                    needleHeightRatio={0.7}
                    ringWidth={20}
                  />
                ) : (
                  <span className="font-bold">{value}</span>
                )}
              </td>
            );
          },
        }}
      />
      {!isPrint && (
        <NextQuestionModal question={modalQuestion} onClose={handleClose} />
      )}
    </>
  );
};

export default SurveyTable;
